import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { projectsProjectReferenceAccessHoldCapability } from '@crest/module-projects/contracts/project-reference';
import { platformOptionalFactProviderHostCapability, platformPortalAccessScopeProviderHostCapability, platformPortalHostCapability, } from '@crest/platform-contracts/hosts';
import { estimatingEstimatesCapability, estimatingEstimateVersionsCapability, estimatingProjectEstimateSummariesCapability, } from './contracts/estimating-estimate.contract.js';
import { estimatingEstimateTemplateApplicationCapability, estimatingEstimateTemplatesCapability, estimatingEstimateTemplateVersionsCapability, } from './contracts/estimating-estimate-template.contract.js';
import { estimatingPricebookCapability, estimatingPricebookImportsCapability, estimatingPricebookVersionsCapability, } from './contracts/estimating-pricebook.contract.js';
import { estimatingProposalPackagesCapability } from './contracts/estimating-proposal-package.contract.js';
import { estimatingProposalContentCapability } from './contracts/estimating-proposal-content.contract.js';
import { estimatingXactimateExportJobsCapability, estimatingXactimateNormalizationCapability, estimatingXactimateSketchSpecificationsCapability, } from './contracts/estimating-xactimate.contract.js';
import { estimatingImportCapability } from './contracts/estimating-import.contract.js';
import { estimatingInsuranceClaimsCurrentEstimateProviderId, estimatingInsuranceClaimsReadinessProviderId, } from './contracts/estimating-insurance-claims-provider.contract.js';
import { estimatingFinanceBillableRevisionProviderId, estimatingFinanceBillableRevisionsCapability, } from './contracts/estimating-finance-billable-revision.contract.js';
import { estimatingFinanceProfitabilityContractValuesCapability, estimatingFinanceProfitabilityContractValuesProviderId, } from './contracts/estimating-finance-profitability-contract-values.contract.js';
import { estimatingPortalProposalCapability, estimatingPortalProposalReadGrant, estimatingPortalProposalSelectGrant, estimatingPortalProposalScopeProviderId, } from './contracts/estimating-portal-proposal.contract.js';
import { estimatingDocumentsProposalAcceptanceFactsCapability, estimatingDocumentsProposalAcceptanceFactsProviderId, } from './contracts/estimating-documents-proposal-acceptance.contract.js';
export const estimatingModuleManifest = defineCrestModule({
    id: 'estimating',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        'projects.project-reference.v1',
        projectsProjectReferenceAccessHoldCapability,
        'projects.project-access-decision.v2',
        platformOptionalFactProviderHostCapability,
        platformPortalAccessScopeProviderHostCapability,
        platformPortalHostCapability,
    ],
    optionalCapabilities: [
        'documents.instances.v1',
        'communications.messages.v1',
        'scanning-media.scan-assembly.v1',
        'mitigation.drying-report-data.v1',
    ],
    optionalCapabilityFallbacks: [
        {
            capability: 'documents.instances.v1',
            degradedMode: 'estimating.degraded.no-generated-proposal.v1',
        },
        {
            capability: 'communications.messages.v1',
            degradedMode: 'estimating.degraded.no-provider-delivery.v1',
        },
        {
            capability: 'scanning-media.scan-assembly.v1',
            degradedMode: 'estimating.degraded.manual-quantities.v1',
        },
        {
            capability: 'mitigation.drying-report-data.v1',
            degradedMode: 'estimating.degraded.manual-scope-facts.v1',
        },
    ],
    provides: [
        estimatingEstimatesCapability,
        estimatingEstimateVersionsCapability,
        estimatingProjectEstimateSummariesCapability,
        estimatingPricebookCapability,
        estimatingPricebookVersionsCapability,
        estimatingPricebookImportsCapability,
        estimatingEstimateTemplatesCapability,
        estimatingEstimateTemplateVersionsCapability,
        estimatingEstimateTemplateApplicationCapability,
        estimatingProposalPackagesCapability,
        estimatingProposalContentCapability,
        estimatingXactimateNormalizationCapability,
        estimatingXactimateSketchSpecificationsCapability,
        estimatingXactimateExportJobsCapability,
        estimatingImportCapability,
        estimatingFinanceBillableRevisionsCapability,
        estimatingFinanceProfitabilityContractValuesCapability,
        estimatingPortalProposalCapability,
        estimatingDocumentsProposalAcceptanceFactsCapability,
    ],
    permissions: [
        'estimating.estimates.read',
        'estimating.estimates.manage',
        'estimating.estimates.release',
        'estimating.estimates.accept',
        'estimating.estimates.void',
        'estimating.pricebook.read',
        'estimating.pricebook.manage',
        'estimating.pricebook.import',
        'estimating.templates.read',
        'estimating.templates.manage',
        'estimating.packages.manage',
        'estimating.xactimate.read',
        'estimating.xactimate.export',
        'estimating.xactimate.retry',
        'estimating.import.read',
        'estimating.import.execute',
        'estimating.import.rollback',
        estimatingPortalProposalReadGrant,
        estimatingPortalProposalSelectGrant,
    ],
    settings: [
        'estimating.estimates.configuration',
        'estimating.estimates.display',
        'estimating.copy.estimates',
        'estimating.proposals.display',
        'estimating.copy.proposals',
        'estimating.portal-proposals.configuration',
        'estimating.portal-proposals.display',
        'estimating.copy.portal-proposals',
        'estimating.catalog.configuration',
        'estimating.catalog.display',
        'estimating.copy.catalog',
        'estimating.xactimate.configuration',
        'estimating.xactimate.display',
        'estimating.copy.xactimate',
    ],
    copyCatalogs: [
        'estimating.copy.estimates', 'estimating.copy.proposals',
        'estimating.copy.portal-proposals',
        'estimating.copy.catalog', 'estimating.copy.xactimate',
    ],
    events: {
        publishes: [
            'estimating.estimate-created.v1',
            'estimating.estimate-revised.v1',
            'estimating.estimate-released.v1',
            'estimating.estimate-viewed.v1',
            'estimating.estimate-accepted.v1',
            'estimating.estimate-declined.v1',
            'estimating.estimate-expired.v1',
            'estimating.estimate-voided.v1',
            'estimating.estimate-duplicated.v1',
            'estimating.pricebook-category-created.v1',
            'estimating.pricebook-category-revised.v1',
            'estimating.pricebook-category-archived.v1',
            'estimating.pricebook-category-restored.v1',
            'estimating.pricebook-item-created.v1',
            'estimating.pricebook-item-revised.v1',
            'estimating.pricebook-item-archived.v1',
            'estimating.pricebook-item-restored.v1',
            'estimating.pricebook-imported.v1',
            'estimating.estimate-template-created.v1',
            'estimating.estimate-template-revised.v1',
            'estimating.estimate-template-archived.v1',
            'estimating.estimate-template-restored.v1',
            'estimating.proposal-package-generated.v1',
            'estimating.xactimate-export-job-created.v1',
            'estimating.xactimate-export-job-retried.v1',
            'estimating.xactimate-export-job-compiled.v1',
            'estimating.xactimate-export-job-failed.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'estimating.routes.estimates', priority: 0 },
            { id: 'estimating.routes.pricebook', priority: 10 },
            { id: 'estimating.routes.templates', priority: 20 },
            { id: 'estimating.routes.proposal-packages', priority: 30 },
            { id: 'estimating.routes.xactimate', priority: 40 },
        ],
        jobs: [
            { id: 'estimating.jobs.expire-estimates', priority: 0 },
        ],
        subscribers: [],
        slots: [{
                id: estimatingPortalProposalScopeProviderId,
                requiredCapability: platformPortalAccessScopeProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 0,
            }, {
                id: estimatingFinanceBillableRevisionProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 0,
            }, {
                id: estimatingFinanceProfitabilityContractValuesProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 10,
            }, {
                id: estimatingDocumentsProposalAcceptanceFactsProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 20,
            }, {
                id: estimatingInsuranceClaimsCurrentEstimateProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 0,
            }, {
                id: estimatingInsuranceClaimsReadinessProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'estimating.estimates.read',
                priority: 1,
            }],
    },
    web: {
        routes: [
            { id: 'estimating.web.estimates', priority: 0 },
            { id: 'estimating.web.proposals', priority: 5 },
            { id: 'estimating.web.pricebook', priority: 10 },
            { id: 'estimating.web.estimate-templates', priority: 20 },
            { id: 'estimating.web.xactimate', priority: 30 },
        ],
        navigation: [
            { id: 'estimating.navigation.estimates', priority: 55 },
            { id: 'estimating.navigation.proposals', priority: 56 },
            { id: 'estimating.navigation.pricebook', priority: 57 },
            { id: 'estimating.navigation.estimate-templates', priority: 58 },
            { id: 'estimating.navigation.xactimate', priority: 59 },
        ],
        slots: [{
                id: 'estimating.web.projects.estimate-summary',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'estimating.estimates.read',
                priority: 45,
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: {
        routes: [],
        slots: [{
                id: 'estimating.portal.project-proposal',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: estimatingPortalProposalReadGrant,
                priority: 20,
            }],
    },
    database: {
        schema: 'estimating',
        owns: [
            'estimating.estimate_number_counters',
            'estimating.estimates',
            'estimating.estimate_versions',
            'estimating.estimate_version_lines',
            'estimating.estimate_acceptances',
            'estimating.estimate_events',
            'estimating.estimate_commands',
            'estimating.reject_immutable_change',
            'estimating.current_membership_can_read_estimate',
            'estimating.current_membership_can_manage_estimate',
            'estimating.estimate_version_record',
            'estimating.estimate_record',
            'estimating.write_estimate_version',
            'estimating.count_estimates',
            'estimating.get_estimate_command_receipt',
            'estimating.execute_estimate_command',
            'estimating.list_estimates',
            'estimating.get_estimate',
            'estimating.list_estimate_versions',
            'estimating.read_project_estimate_summary',
            'estimating.pricebook_categories',
            'estimating.pricebook_category_versions',
            'estimating.pricebook_items',
            'estimating.pricebook_item_versions',
            'estimating.pricebook_events',
            'estimating.pricebook_imports',
            'estimating.pricebook_commands',
            'estimating.current_membership_can_read_pricebook',
            'estimating.current_membership_can_manage_pricebook',
            'estimating.pricebook_category_record',
            'estimating.pricebook_item_version_record',
            'estimating.pricebook_item_record',
            'estimating.get_pricebook_command_receipt',
            'estimating.execute_pricebook_category_command',
            'estimating.list_pricebook_categories',
            'estimating.get_pricebook_category',
            'estimating.find_pricebook_category',
            'estimating.execute_pricebook_item_command',
            'estimating.count_pricebook_items',
            'estimating.list_pricebook_items',
            'estimating.get_pricebook_item',
            'estimating.find_pricebook_item',
            'estimating.list_pricebook_item_versions',
            'estimating.read_current_pricebook_rates',
            'estimating.pricebook_import_record',
            'estimating.record_pricebook_import',
            'estimating.get_pricebook_import_by_command',
            'estimating.read_pricebook_summary',
            'estimating.estimate_templates',
            'estimating.estimate_template_versions',
            'estimating.estimate_template_version_lines',
            'estimating.estimate_template_events',
            'estimating.estimate_template_commands',
            'estimating.current_membership_can_read_templates',
            'estimating.current_membership_can_manage_templates',
            'estimating.estimate_template_version_record',
            'estimating.estimate_template_record',
            'estimating.get_estimate_template_command_receipt',
            'estimating.write_estimate_template_version',
            'estimating.execute_estimate_template_command',
            'estimating.count_estimate_templates',
            'estimating.list_estimate_templates',
            'estimating.get_estimate_template',
            'estimating.list_estimate_template_versions',
            'estimating.proposal_packages',
            'estimating.proposal_package_members',
            'estimating.proposal_package_commands',
            'estimating.proposal_package_selections',
            'estimating.estimate_origins',
            'estimating.record_estimate_origin',
            'estimating.proposal_package_record',
            'estimating.record_proposal_package',
            'estimating.get_proposal_package_by_command',
            'estimating.get_proposal_package',
            'estimating.list_proposal_packages',
            'estimating.get_proposal_package_selection',
            'estimating.capture_proposal_package_selection',
            'estimating.read_portal_proposal',
            'estimating.execute_portal_proposal_selection',
            'estimating.execute_portal_proposal_decline',
            'estimating.list_replaceable_proposal_package_drafts',
            'estimating.xactimate_export_jobs',
            'estimating.xactimate_export_attempts',
            'estimating.xactimate_export_commands',
            'estimating.xactimate_export_job_record',
            'estimating.count_active_xactimate_export_jobs',
            'estimating.get_xactimate_export_command_receipt',
            'estimating.create_xactimate_export_job',
            'estimating.retry_xactimate_export_job',
            'estimating.list_xactimate_export_jobs',
            'estimating.get_xactimate_export_job',
            'estimating.claim_xactimate_export_job',
            'estimating.heartbeat_xactimate_export_job',
            'estimating.complete_xactimate_export_job',
            'estimating.fail_xactimate_export_job',
            'estimating.expire_due_estimates',
            'estimating.import_domain_totals_valid',
            'estimating.import_runs',
            'estimating.import_identity_map',
            'estimating.import_row_receipts',
            'estimating.import_rejects',
            'estimating.import_rollback_receipts',
            'estimating.legacy_import_target_changes',
            'estimating.legacy_import_target_rollbacks',
            'estimating.begin_import_run',
            'estimating.record_import_reject',
            'estimating.record_import_receipt',
            'estimating.complete_import_run',
            'estimating.fail_import_run',
            'estimating.mark_import_run_rolled_back',
            'estimating.get_import_run',
            'estimating.get_import_run_evidence',
            'estimating.apply_legacy_import_action_v1',
            'estimating.verify_legacy_import_receipt_v1',
            'estimating.rollback_legacy_import_action_v1',
            'estimating.verify_import_resolution_v1',
        ],
        readsThrough: [
            'projects.project-reference.v1',
            projectsProjectReferenceAccessHoldCapability,
            'projects.project-access-decision.v2',
        ],
        migrations: [
            'estimating.estimate-foundation',
            'estimating.pricebook-templates-packages',
            'estimating.xactimate-expiry-workers',
            'estimating.import-reconciliation',
            'estimating.legacy-import-writes',
            'estimating.proposal-content',
            'estimating.portal-proposals',
            'estimating.project-reference-mutation-holds',
        ],
    },
    health: ['estimating.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 2,
            queueBacklogLimit: 1_000,
            databaseConnectionBudget: 10,
            workerIsolation: 'separate-worker',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'Estimating owns retained versions and facts; consumers reconcile from its public summaries.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['estimating.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Estimating records, source manifests, identity maps, reconciliation and rollback evidence, lifecycle facts, and command receipts remain stored while disabled.',
});
//# sourceMappingURL=estimating.module.js.map